import { fn } from '@ember/helper';
import { action, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { getLocalDateInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import GeneralButtonsButtonsPreviousNextSmall from 'lumen/components/general/buttons/buttons-previous-next-small';
import GeneralCalendarsCalendarDay from 'lumen/components/general/calendars/calendar-day';
import GeneralDatepickersDatepickerWithCompare from 'lumen/components/general/datepickers/datepicker-with-compare';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsSidebar from 'lumen/components/general/dropdowns/sidebar';
import GeneralDropdownsTriggersTriggerDropdownBasic from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-basic';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';

export default class GeneralDatepickersPickerDay extends GeneralDatepickersDatepickerWithCompare {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked tempSelectedDay = null;
  @tracked year = dayjs().format('YYYY');

  // Used for the outside click detection (see ota-dropdown component)
  dropdownClass = '.day-selection-dropdown';

  triggerComponent = GeneralDropdownsTriggersTriggerDropdownBasic;

  // The following two CPs override the base class CPs
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beginReached() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.prevOffsetDisabled;
  }

  /**
   * This is only used in the calendar
   * We need to mask the days timezone because calendar can't handle anything other than UTC
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarDay() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const day = this.tempSelectedDay ?? this.args.selectedDay;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return dayjs(day.format('YYYY-MM-DD'), 'YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayText() {
    // @ts-expect-error: Incorrect type
    if (this.args.selectedDay) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return this.args.selectedDay.format('ll');
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endReached() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.nextOffsetDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedDayChanged() {
    return (
      this.tempSelectedDay !== null &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      !this.tempSelectedDay.isSame(this.args.selectedDay, 'day')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isApplyDisabled() {
    return !this.hasSelectedDayChanged && !this.hasCompareChanged;
  }

  @action
  // @ts-expect-error: Incorrect type
  applyChanges(dropdown): void {
    this.updateSelectedDay();
    this.updateCompare();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    dropdown.toggleDropdown();
  }

  @action
  didReceivePickerDayAttrs(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    set(this, 'year', this.args.selectedDay.format('YYYY'));
  }

  @action
  nextRange(): void {
    if (!this.endReached) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.increasePeriod();
    }
  }

  @action
  onCancel(): void {
    this.revertDateCompare();
  }

  @action
  previousRange(): void {
    if (!this.beginReached) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.decreasePeriod();
    }
  }

  @action
  resetTempSelectedDay(): void {
    this.tempSelectedDay = null;
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  selectDay(day) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { moment } = day;

    // Don't use the moment object directly, as it clashes with dayjs objects.
    // E.g. dayjs.isSame(moment) will always return false, even if it's the same day.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const dayjsDay = dayjs(moment.toDate());

    if (this.isWithinRange(dayjsDay)) {
      const shouldDateCompareBeDisabled = dayjsDay.isBefore(
        getLocalDateInBrowserTimezone(this.hotelAndMarketService.timezone),
        'day',
      );
      if (this.isDateCompareDisabled && !shouldDateCompareBeDisabled) {
        this.disableInvalidDateCompares(false);
      } else if (!this.isDateCompareDisabled && shouldDateCompareBeDisabled) {
        this.disableInvalidDateCompares(true);
      }

      // @ts-expect-error: Incorrect type
      this.tempSelectedDay = dayjsDay;
    }
  }

  @action
  updateSelectedDay(): void {
    if (this.hasSelectedDayChanged) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.selectionsChanged(
        'day',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.tempSelectedDay.format('YYYY-MM-DD'),
      );
    }
    this.tempSelectedDay = null;
  }

  <template>
    <div
      {{didInsert this.didReceivePickerDayAttrs}}
      {{didUpdate this.didReceivePickerDayAttrs}}
      class="flex-date-picker-container layout-row flex items-center justify-start"
    >
      <div class="mr-300">
        <GeneralButtonsButtonsPreviousNextSmall
          @beginReached={{this.beginReached}}
          @endReached={{this.endReached}}
          @nextClicked={{this.nextRange}}
          @previousClicked={{this.previousRange}}
        />
      </div>

      <GeneralDropdownsDropdownYield
        {{! @glint-expect-error: Incorrect type }}
        @selected={{this.dayText}}
        @triggerComponent={{this.triggerComponent}}
        as |dropdown|
      >
        <section class="date-picker-with-side-bar-calendar">
          <section class="date-picker-with-side-bar-calendar__content">
            <div class="mb-500">
              <GeneralCalendarsCalendarDay
                {{! @glint-expect-error: Incorrect type }}
                @displayEvents={{true}}
                @maxDate={{@maxDate}}
                @minDate={{@minDate}}
                @selectDay={{this.selectDay}}
                @selected={{this.calendarDay}}
              />
            </div>

            <GeneralDropdownsSidebar
              @selectOption={{this.selectOption}}
              @sidebarLists={{this.convertedSidebarLists}}
              {{! @glint-expect-error: Incorrect type }}
              {{didInsert this.initSelectedDateCompare}}
              {{! @glint-expect-error: Incorrect type }}
              {{didInsert this.resetTempSelectedDay}}
            />
          </section>

          <section class="date-picker-with-side-bar-calendar__footer">
            <div class="datepicker-save-cancel">
              <GeneralFormFieldsCancelAndConfirm
                @cancel={{fn this.cancelChanges dropdown}}
                @confirm={{fn this.applyChanges dropdown}}
                @isConfirmDisabled={{this.isApplyDisabled}}
              />
            </div>
          </section>
        </section>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>
}
